import { alpha, ButtonBase, ClickAwayListener, styled, useMediaQuery } from '@mui/material';
import { useRef, useState } from 'react';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { LongPressEventType, useLongPress } from 'use-long-press';
import { formatReactors, ReactionSummary } from './group-reactions';
import {
  REACTION_LONG_PRESS_MOVE_TOLERANCE_PX,
  REACTION_LONG_PRESS_MS,
} from './reactions.constants';

const ChipRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'reactedByMe' && prop !== 'isInteractive',
})<{ reactedByMe: boolean; isInteractive: boolean }>(({
  theme,
  reactedByMe,
  isInteractive,
}) => {
  const { sys } = theme.palette.custom.OV;
  const { selectedOpacity, focusOpacity, disabledOpacity } = theme.palette.action;
  // A see-through tint of whatever card the chip sits on; yours takes the action color.
  const tint = reactedByMe ? sys.primary : sys.onSurface;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.spacing(2),
    border: '1px solid',
    borderColor: reactedByMe ? alpha(tint, disabledOpacity) : 'transparent',
    backgroundColor: alpha(tint, reactedByMe ? focusOpacity : selectedOpacity),
    color: reactedByMe ? sys.primary : sys.onSurfaceVariant,
    cursor: isInteractive ? 'pointer' : 'default',
    transition: theme.transitions.create(['background-color', 'border-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    ...(isInteractive && {
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: alpha(tint, focusOpacity + selectedOpacity),
      },
    }),
  };
});

const ChipEmoji = styled('span')(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1,
}));

const ChipCount = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1,
}));

export interface ReactionChipProps {
  reaction: ReactionSummary;
  /** When false the chip only shows who reacted; clicking it does nothing. */
  isInteractive: boolean;
  onToggle: () => void;
  notice?: string;
}

// Muted against whichever surface the tooltip has: dark in v1, light in v2.
const NoticeText = styled('span')(({ theme }) => ({
  display: 'block',
  opacity: 1 - theme.palette.action.disabledOpacity,
}));

const ReactionTooltipContent = ({
  reaction,
  notice,
}: {
  reaction: ReactionSummary;
  notice?: string;
}) => {
  return (
    <>
      {formatReactors(reaction)} reacted with {reaction.emoji}
      {notice ? <NoticeText>{notice}</NoticeText> : null}
    </>
  );
};

/**
 * One emoji and its count. Clicking adds or removes your reaction. Who reacted shows on hover,
 * or on touch devices on a long press (or a tap when the chip is read-only).
 */
export const ReactionChip = ({
  reaction,
  isInteractive,
  onToggle,
  notice,
}: ReactionChipProps) => {
  const { emoji, count, reactedByMe } = reaction;
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const [isTouchTooltipOpen, setIsTouchTooltipOpen] = useState(false);
  // Some browsers still fire a click when a long press is released; it must not toggle.
  const wasLongPressRef = useRef(false);

  const longPressHandlers = useLongPress(
    isTouchDevice
      ? () => {
          wasLongPressRef.current = true;
          setIsTouchTooltipOpen(true);
        }
      : null,
    {
      threshold: REACTION_LONG_PRESS_MS,
      detect: LongPressEventType.Touch,
      cancelOnMovement: REACTION_LONG_PRESS_MOVE_TOLERANCE_PX,
      onStart: () => {
        wasLongPressRef.current = false;
      },
    },
  );

  const handleClick = () => {
    if (wasLongPressRef.current) {
      wasLongPressRef.current = false;
      return;
    }
    if (isInteractive) {
      onToggle();
    } else if (isTouchDevice) {
      setIsTouchTooltipOpen((isOpen) => !isOpen);
    }
  };

  const chip = (
    <Tooltip
      describeChild
      title={<ReactionTooltipContent reaction={reaction} notice={notice} />}
      {...(isTouchDevice && {
        open: isTouchTooltipOpen,
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
      })}
    >
      <ChipRoot
        reactedByMe={reactedByMe}
        isInteractive={isInteractive}
        aria-pressed={reactedByMe}
        aria-disabled={!isInteractive}
        aria-label={`${emoji} ${count}`}
        onClick={handleClick}
        {...longPressHandlers()}
      >
        <ChipEmoji aria-hidden>{emoji}</ChipEmoji>
        <ChipCount aria-hidden>{count}</ChipCount>
      </ChipRoot>
    </Tooltip>
  );

  if (!isTouchDevice) {
    return chip;
  }

  return (
    <ClickAwayListener onClickAway={() => setIsTouchTooltipOpen(false)}>
      {chip}
    </ClickAwayListener>
  );
};
